import { Component, computed, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { EMPTY, of } from 'rxjs';
import { EFLMaxDisconnectionTimeDto, EFLMaximumZsDto, MceApiService } from '../../../core/mceapi.client.generated';
import { apiOpt, appOpt, getNoneOptionDto } from '../../../core/mceapi.client.helpers';
import { InfoMessageComponent } from '../../../shared/components/info-message/info-message.component';
import { isNone, match, unwrap } from '../../../shared/functions/functional';
import { CableCalculationsStore } from '../cable-calculations.store';
import { EditCable } from '../cable-edit.model';
import { createFaultSourceDto } from '../fault-source/fault-source.model';
import { createLoadDto } from '../load/load.model';
import { createProtectionDto } from '../protection/cable-protection.component';
import { createRCDDto } from '../protection/rcd/rcd.component';
import { createSystemVoltageDto } from '../system-voltage/system-voltage.model';
import { createEarthFaultLoopDto } from './cable-efl.model';
import { CalculationBoxComponent } from '../../../shared/components/calculation-box/calculation-box.component';
import { createEarthingDto } from '../earthing/earthing.model';

function formatOhms(value: number | undefined): string {
  return value === undefined ? 'N/A' : `${value.roundTo(6)}  \u03A9`;
}


@Component({
  selector: 'cable-edit-efl',
  imports: [FormField, MatInputModule, MatCheckboxModule, InfoMessageComponent, CalculationBoxComponent],
  templateUrl: './cable-efl.component.html',
})
export class CableEFLComponent {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);
  readonly store = inject(CableCalculationsStore);

  // maybe need to send required with original DTO to get started, then check here as things change

  requiredRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      load: createLoadDto(this.form().load)(),
      protection: apiOpt(createProtectionDto(this.form().protection)()),
      earthing: createEarthingDto(this.form().earthing)(),
      isDirty: this.form()().dirty(),
      valid: this.form().systemVoltage().valid() && this.form().load().valid() && this.form().protection().valid() && this.form().earthing().valid(),
    }),
    stream: ({ params }) => {
      if (!params.isDirty || !params.valid) { return of(undefined); }

      return this.mceApiService.v1EflRequired({
        systemVoltage: params.systemVoltage,
        load: params.load,
        device: params.protection,
        earthing: params.earthing, });
    }
  });
  _required = effect(() => {
    let required = this.requiredRx.value()?.value;
    if (required === undefined || !this.form()().dirty()) return;
    this.form().earthFaultLoop.eflRequired().value.set(required);
  })


  eflRx = rxResource({
    params: () => ({
      earthFaultLoop: createEarthFaultLoopDto(this.form().earthFaultLoop)(),
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      load: createLoadDto(this.form().load)(),
      protection: createProtectionDto(this.form().protection)(),
      rcd: createRCDDto(this.form().rcd)(),
      earthing: createEarthingDto(this.form().earthing)(),
      cableImpedance: this.store.impedance(),
      modelFaultSource: createFaultSourceDto(this.form().faultSource)(),
      valid: this.form().earthFaultLoop().valid() && this.form().systemVoltage().valid() && this.form().load().valid() && this.form().protection().valid() && this.form().earthing().valid() && this.store.impedance() !== undefined && this.form().faultSource().valid(),
    }),
    stream: ({ params }) => {

      if (params.cableImpedance === undefined || isNone(params.earthFaultLoop) || !params.valid) {
        return of(undefined);
      }

      return this.mceApiService.v1Efl({
        userValues: unwrap(params.earthFaultLoop),
        systemVoltage: params.systemVoltage,
        load: params.load,
        device: apiOpt(params.protection),
        rcd: apiOpt(params.rcd),
        earthing: params.earthing,
        cableImpedance: params.cableImpedance,
        modelFaultSource: params.modelFaultSource,
      });
    }
  });
  efl = computed(() => this.eflRx.value());
  viewState = computed(() => ({
    isLoading: this.eflRx.isLoading(),
    showEFLCalculation: this.efl() !== undefined,
    required: this.form().earthFaultLoop.eflRequired().value(),

    status: this.efl()?.status ?? { case: 'success', message: '' },
    zs: formatOhms(this.efl()?.zs),
    maxZs: match(() => 'N/A', (x:EFLMaximumZsDto) => formatOhms(x.value))(appOpt(this.efl()?.maxZs ?? getNoneOptionDto() )),
    t: match(() => 'N/A', (x: EFLMaxDisconnectionTimeDto) => x.value === undefined ? 'N/A' : `${x.value}  s`)(appOpt(this.efl()?.maxDisconnectionTime ?? getNoneOptionDto())),
  }));

}
